import * as React from 'react';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import MultiBackend, { TouchTransition } from 'react-dnd-multi-backend';
import TouchBackend from 'react-dnd-touch-backend';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { getQueryStringServiceFromAngular } from '../../../common/react/QueryString';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { shouldResetPreviousUrl } from '../../../ts/helpers/UrlHelpers';
import { DataSources } from '../../../ts/resources/Datasources';
import { MastheadNotifications } from '../../../ts/services/MastheadNotifications';
import { PostLoginNotifications } from '../../../ts/services/PostLoginNotifications';
import { SiteSwitchService } from '../../../ts/services/SiteSwitchService';
import { StartPageService } from '../../../ts/services/StartPageService';
import { incrementRouterSpaceReactKey } from '../../actions/spaces/IncrementRouterSpaceReactKeyAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../actions/spaces/SetPreviousUrlReducerAction';
import { appStore } from '../../AppStore';
import { bindDisplaySettingsScopeToReduxAndSetInitialValues } from '../../content/DisplaySettingsReduxBindings';
import { bindDisplaySettingsScopeToUrlAndSetInitialValues } from '../../content/DisplaySettingsUrlBindings';
import { ConnectedLeftNav } from '../../layout/ConnectedLeftNav';
import { ConnectedPageHeader } from '../../layout/ConnectedPageHeader';
import { ConnectedSkipBarProvider } from '../../layout/SkipBarProvider';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { AppState } from '../../state/AppState';
import { isMetricSpace } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { CustomDragLayer } from '../CustomDragLayer';
import { ServerSpaceRouter } from '../ServerSpaces/ServerSpaceRouter';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { SiteSpaceRouter, SiteSpaceRouterComponent } from './SiteSpaceRouter';
import { MobileAppBanner } from '../../../mobile/components/MobileAppBanner';

export interface MainOwnProps extends ReactDirectiveProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly fromUnitTests?: boolean; // Hooks don't work well with shallow tests so dirty workaround
}

interface MainPropsFromAngular {
  readonly $location: ng.ILocationService;
  readonly dataSources: DataSources;
  readonly siteSwitchService: SiteSwitchService;
  readonly startPageService: StartPageService;
  readonly postLoginNotifications: PostLoginNotifications;
  readonly mastheadNotifications: MastheadNotifications;
}

export interface MainStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isMetricSpace: boolean;
}

export interface MainDispatchProps {
  readonly setPreviousUrl: (url: string) => void;
  readonly incrementRouterSpaceReactKey: () => void;
  readonly requestSpaceContents: () => void;
}

export type MainProps = MainOwnProps & MainPropsFromAngular & MainStateProps & MainDispatchProps;

export const MainComponent: React.FunctionComponent<MainProps> = props => {

  const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
  const siteNameFromRedux = parseOutSiteUrlName(props.urls.homePath());
  const [path, setPath] = React.useState(siteNameFromCurrentPath === siteNameFromRedux ? props.routeLocationPath : '');

  React.useEffect(() => {
    if (!props.fromUnitTests) {
      const queryString = getQueryStringServiceFromAngular(props.$location);
      bindDisplaySettingsScopeToReduxAndSetInitialValues(props.angularContext.scope as any, appStore);
      bindDisplaySettingsScopeToUrlAndSetInitialValues(props.angularContext.scope as any, appStore, queryString);
    }
  }, [ props.fromUnitTests ]);

  React.useEffect(() => {
    if (props.postLoginNotifications) {
      // See http://mytableau/display/devft/Vizportal+Online+Notifications+Spec
      props.postLoginNotifications.showNotificationsIfEnabled()
        .finally(() => props.mastheadNotifications.showNotificationsIfEnabled());
    }
  }, [ props.postLoginNotifications ]);

  React.useEffect(() => {
    if (props.routeLocationPath !== path) {
      const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
      const isChangingSites = parseOutSiteUrlName(path) !== siteNameFromCurrentPath;
      if (isChangingSites && !_.isUndefined(siteNameFromCurrentPath)) {
        props.siteSwitchService.switchIfNeeded(siteNameFromCurrentPath)
          .finally(() => {
            setPath(props.routeLocationPath);
          });
      } else {
        setPath(props.routeLocationPath);
      }

      // Includes resetting when navigating to /server which isChangingSites will catch
      if (shouldResetPreviousUrl(props.routeLocationPath, isChangingSites)){
        props.setPreviousUrl('');
      }
    }
  }, [props.routeLocationPath]);

  if (props.fromUnitTests) { return null; }

  const HTML5toTouch = {
    backends: [
      {
        backend: HTML5Backend
      },
      {
        backend: TouchBackend,
        options: {enableMouseEvents: true, enableKeyboardEvents: true, delayTouchStart: 200},
        transition: TouchTransition
      }
    ]
  };

  const constrainedClass = props.isMetricSpace ? '' : 'tb-main-constrained';
  const defaultRedirectPath = getDefaultRedirectPath(props.routeLocationPath, props.startPageService);
  return (
    <div className={'tb-column-flex-parent tb-fill'}>
      <ConnectedSkipBarProvider />
      <MobileAppBanner />
      <div className={'tb-flex-fill ' + constrainedClass}>
        <div className={'tb-row-flex-parent tb-full-height'}>
          <ConnectedLeftNav />
          <div className={'tb-flex-fill tb-column-flex-parent tb-main-content'}>
            <ConnectedPageHeader angularContext={props.angularContext} datasourcesResource={props.dataSources}/>
            <div className={'tb-flex-fill'}>
                <CustomDragLayer/>
                <Switch>
                  <Route exact from={props.urls.defaultPath()}>
                    <Redirect exact from={props.urls.defaultPath()} to={defaultRedirectPath}/>
                  </Route>
                  <Route exact from={'/'}>
                    <Redirect exact from={props.urls.defaultPath()} to={defaultRedirectPath}/>
                  </Route>
                  <Route path={'/server'}>
                    <ServerSpaceRouter
                      angularContext={props.angularContext}
                      routeLocationPath={props.routeLocationPath}
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  </Route>
                  <Route>
                    <SiteSpaceRouter
                      isBusy={parseOutSiteUrlName(path) !== siteNameFromCurrentPath}
                      siteName={siteNameFromCurrentPath}
                      angularContext={props.angularContext}
                      routeLocationPath={path} // Don't want to render until the site switch is done and path has been updated
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  </Route>
                </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const parseOutSiteUrlName = (path: string): string  => {
  if (path.startsWith('/site/')) {
    path = path.substring(6); // chop off the initial '/site/'
    const endSiteNamePattern = new RegExp('[/?]');
    const endSiteNameIndex = path.search(endSiteNamePattern);
    if (endSiteNameIndex === -1) {
      return path === '' ? undefined : path;
    }
    const siteUrlName = path.substring(0, endSiteNameIndex);
    return siteUrlName === '' ? undefined : siteUrlName;
  }

  // if its a valid site space path return empty string to indicate default
  const urls = new SpacesSiteUrls({ siteUrlName: '' });
  const basePaths = SiteSpaceRouterComponent.getWatchedBasePaths(urls);
  if (_.some(basePaths, (basePath) => path.startsWith(basePath))) {
    return '';
  }

  // these should be /server/ paths or invalid paths
  return undefined;
};

const getDefaultRedirectPath = (routeLocationPath: string, startPageService: StartPageService): string => {
  const siteUrlName = parseOutSiteUrlName(routeLocationPath);
  if (!_.isUndefined(siteUrlName) && siteUrlName.length > 0) {
    return startPageService.getCurrentUserStartPageForSite(siteUrlName);
  } else {
    return startPageService.getCurrentUserStartPageForCurrentSite();
  }
};

export namespace MainAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      dataSources: ServiceLocator.get<DataSources>('DataSources'),
      $location: ServiceLocator.get<ng.ILocationService>('$location'),
      siteSwitchService: ServiceLocator.get<SiteSwitchService>('SiteSwitchService'),
      startPageService: ServiceLocator.get<StartPageService>('StartPageService'),
      postLoginNotifications: ServiceLocator.get<PostLoginNotifications>('PostLoginNotifications'),
      mastheadNotifications: ServiceLocator.get<MastheadNotifications>('MastheadNotifications'),
    };
  }

  function selectPropsFromAngularServices(angularServices: MainPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<MainOwnProps, MainPropsFromAngular, MainPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const mapStateToProps: MapStateToProps<MainStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  isMetricSpace: isMetricSpace(selectSpaceInfo(state))
});

const mapDispatchToProps: MapDispatchToProps<MainDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey,
  setPreviousUrl,
  requestSpaceContents,
}, dispatch);

export const ConnectedMainComponent: React.ComponentClass<MainOwnProps & MainPropsFromAngular>
  = connect(mapStateToProps, mapDispatchToProps)(MainComponent);

export const Main: React.ComponentType<MainOwnProps> = MainAngularProvider.HOC(ConnectedMainComponent);
